import React, { useCallback, useEffect, useState } from 'react';

import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Slider from '@mui/material/Slider';
import useTheme from '@mui/material/styles/useTheme';
import Typography from '@mui/material/Typography';

import { SketchPicker } from 'react-color';
import { COLORS } from '../../../utils/colors';
import { useInspectorStyles } from '../styles';

import ArrowDropDown from '@mui/icons-material/ArrowDropDown';
import ArrowOutward from '@mui/icons-material/ArrowOutward';
import CircleOutlined from '@mui/icons-material/CircleOutlined';
import Close from '@mui/icons-material/Close';
import DrawOutlined from '@mui/icons-material/DrawOutlined';
import LayersIcon from '@mui/icons-material/Layers';
import SaveOutlined from '@mui/icons-material/SaveOutlined';
import SquareOutlined from '@mui/icons-material/SquareOutlined';
import SquareRounded from '@mui/icons-material/SquareRounded';
import Undo from '@mui/icons-material/Undo';

import CustomModal from '../../../components/CustomModal';
import { useSessionContext } from '../../../contexts/SessionContext';
import useSession from '../../../hooks/useSession';
import { CanvasSketch } from '../../../components/CanvasSketch';
import { CanvasImage } from '../../../components/CanvasImage';

import { useAppMessage, useParticipantIds } from '@daily-co/daily-react';

import { useCanvasSketchContext } from '../../../contexts/CanvasSketchContext';
import SaveIcon from '../../../icons/SaveIcon';

interface ICanvasSketch {
  canvasSketchSize: any;
}

const CanvasView = ({ canvasSketchSize }: ICanvasSketch) => {
  const theme = useTheme();
  const styles = useInspectorStyles();

  const {
    setSendPayload,
    color,
    setColor,
    activeShape,
    activeTab,
    setActiveTab,
    penSize,
    setPenSize,
    isPenVisible,
    isLayersVisible,
    strokeList,
    isStrokeListUpdated,
    isColorPaletteOpen,
    handleClickOutside,
    handleToggleShapesModal,
    handleSetActiveShape,
    handleToggleColorPalette,
    setCroppedUndoImages,
    setIsPenVisible,
    setIsLayersVisible,
    isShapesVisible,
    setIsStrokeListUpdated,
    setStrokeList,
    shapesRef,
    paletteRef,
    undoChanges,
    clearCanvas,
  } = useCanvasSketchContext();

  const [isCloseEditorModalOpen, setIsCloseEditorModalOpen] =
    useState<boolean>(false);

  const sendAppMessage = useAppMessage({
    onAppMessage: ({ data, ...rest }: any) => {
      //if (data?.type !== 'media')
      //  console.log(`::::screen canvas handleAppMessage: ${JSON.stringify(data, null, 3)}`)
    },
  });

  const {
    booking,
    localParticipantId,
    client,
    imagesMapRef,
    strokesLayer,
    setStrokesLayer,
    setSelectedImage,

    isImageLoading,
    selectedImage,
    setSelectedImageElement,
  } = useSessionContext();

  const { updateImage } = useSession();

  const sendPayload = useCallback(
    (data: any) => {
      const payload = {
        senderId: localParticipantId,
        ...data,
      };

      console.log(`::::sendAppMessage payload: ${payload.type}`, payload);
      sendAppMessage(payload, '*');
    },
    [localParticipantId, sendAppMessage],
  );

  useEffect(() => {
    console.log('\n\n::::useEffect NOT SKIPPING setSendPayload for now...\n\n');
    setSendPayload(sendPayload);
  }, [sendPayload]);

  const handleCloseEditorModal = () => {
    setIsCloseEditorModalOpen(false);
  };

  const handleOpenEditorModal = () => {
    if (isStrokeListUpdated) setIsCloseEditorModalOpen(true);
    else handleCloseCanvasSketch();
  };

  const handleSaveImage = async () => {
    if (!booking) return;

    let layer = strokesLayer;
    const image = selectedImage?.id
      ? imagesMapRef?.current?.get(selectedImage.id)
      : undefined;
    if (image) {
      if (!image.strokes) {
        layer = 1;
        image.strokes = [[], strokeList];
      } else {
        const strokes = image.strokes.reduce(
          (acc: any, current: any, index: number) => {
            if (
              (index === layer && strokeList.length > 0) ||
              (index !== layer && current.length > 0)
            ) {
              acc.push(index === layer ? strokeList : current);
            }
            return acc;
          },
          [[]],
        );

        if (layer === 0) ++layer;
        if (layer >= strokes.length) layer = 0;

        image.layer = layer;
        image.strokes = strokes;
      }

      const response = await updateImage({
        bookingId: booking.booking_id,
        image: { id: image.id, strokes: image.strokes, layer },
      });

      if (response === '') alert('Error !');
    } else {
      sendPayload({
        type: 'upload-image',
        booking_id: booking.booking_id,
        folder: `${client.sub}/${booking.booking_session_code}`,
      });
    }

    handleCloseCanvasSketch();
  };

  const handleSelectColor = (color: { hex: string }) => {
    setColor(color.hex);
  };

  const handleCloseCanvasSketch = useCallback(() => {
    if (!selectedImage?.id) {
      sendPayload({
        type: 'end-capture',
        value: true,
      });
    }
    /*
    setSelectedImageElement(null);
    clearCanvas();
    setStrokesLayer(0);
    setIsStrokeListUpdated(false);
    setStrokeList([]);
    setActiveTab('Shape');
    setColor('#FF0000');
    setCroppedUndoImages([]);
    handleCloseEditorModal();
    */
    setSelectedImage(null);
  }, [selectedImage, sendPayload]);

  useEffect(() => {
    if (selectedImage !== null) return;

    setSelectedImageElement(null);
    clearCanvas();
    setStrokesLayer(0);
    setIsStrokeListUpdated(false);
    setStrokeList([]);
    setActiveTab('Shape');
    setColor('#FF0000');
    setCroppedUndoImages([]);
    handleCloseEditorModal();
  }, [selectedImage]);

  const CANVAS_SHAPES = [
    { label: 'SQUARE', Icon: SquareOutlined },
    { label: 'CIRCLE', Icon: CircleOutlined },
    { label: 'ARROW', Icon: ArrowOutward },
  ];

  const CANVAS_ICONS = [
    {
      Icon: Close,
      label: 'Close',
      handleClick: handleOpenEditorModal,
    },
    {
      Icon: Undo,
      label: 'Undo',
      handleClick: () => undoChanges(),
    },
    {
      Icon:
        activeShape === 'SQUARE'
          ? SquareOutlined
          : activeShape === 'CIRCLE'
            ? CircleOutlined
            : activeShape === 'ARROW'
              ? ArrowOutward
              : SquareOutlined,
      label: 'Shape',
      handleClick: (event: any) => {
        event.stopPropagation();
        handleClickOutside();
        handleToggleShapesModal();
      },
    },
    {
      Icon: DrawOutlined,
      label: 'Draw',
      handleClick: (event: any) => {
        event.stopPropagation();
        handleClickOutside();
        setIsPenVisible(!isPenVisible);
        setActiveTab('DRAW');
      },
    },
    {
      Icon: SquareRounded,
      label: 'Colour',
      handleClick: (event: any) => {
        event.stopPropagation();
        handleClickOutside();
        handleToggleColorPalette(!isColorPaletteOpen);
      },
    },
    /*
    {
      Icon: LayersIcon,
      label: 'Layers',
      handleClick: (event: any) => {
        event.stopPropagation();
        handleClickOutside();
        setIsLayersVisible(!isLayersVisible);
      },
    },
    */
  ];

  useEffect(() => {
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  const handleSlider = (event: Event, value: number | number[]) => {
    setPenSize(value as number);
  };

  return (
    <>
      <CustomModal
        message={'Are you sure you want to discard changes?'}
        primaryText={'Discard changes'}
        handlePrimary={handleCloseCanvasSketch}
        secondaryText={'Cancel'}
        handleSecondary={handleCloseEditorModal}
        isModalOpen={isCloseEditorModalOpen}
      />

      <Box
        style={styles.room}
        sx={{ background: 'transparent' }}
        display={selectedImage ? 'flex' : 'none'}>
        <Box sx={styles.canvasTools}>
          {CANVAS_ICONS.map(({ Icon, label, handleClick }, index) => {
            return (
              <Box
                bgcolor={activeTab === label ? COLORS.LIGHT_GRAY : '#fff'}
                key={index}
                sx={{
                  ...styles.canvasIcon,
                  display: 'flex',
                }}
                color="black"
                position="relative">
                <IconButton
                  disableRipple
                  onClick={handleClick}
                  color="inherit"
                  sx={{ paddingBottom: '0.1rem' }}
                  disabled={label === 'Undo' && !strokeList.length}>
                  <Icon
                    sx={{
                      fontSize: '1.305rem',
                      color: label === 'Colour' ? color : 'inherit',
                    }}
                  />
                </IconButton>
                <Box display="flex" alignItems="flex-end">
                  <Typography
                    fontSize=".55rem"
                    textAlign="center"
                    fontWeight={500}>
                    {label}
                  </Typography>
                  {(label === 'Shape' || label === 'Layers') && (
                    <ArrowDropDown style={{ fontSize: '.875rem' }} />
                  )}
                </Box>
              </Box>
            );
          })}

          <Box
            sx={{
              ...styles.sidebarHangUp,
              mt: '3rem',
              color: COLORS.PRIMARY,
              border: '.125rem solid #ccc',
              borderColor: COLORS.PRIMARY,
              borderRadius: '2rem',
              backgroundColor: '#fff',
              '&:hover': {
                backgroundColor: COLORS.PRIMARY,
                color: '#fff',
              },
            }}>
            <IconButton
              onClick={() => handleSaveImage()}
              sx={{ color: 'inherit' }}>
              <SaveIcon
                strokeWidth={2}
                sx={{
                  color: 'inherit',
                  fontSize: '1.8rem',
                }}
              />
            </IconButton>
          </Box>
        </Box>

        {isShapesVisible && (
          <Box
            ref={shapesRef}
            sx={styles.canvasShapes}
            border={`1px solid ${theme.palette.divider}`}>
            {CANVAS_SHAPES.map(({ label, Icon }) => {
              return (
                <Box
                  key={label}
                  display="flex"
                  width="100%"
                  bgcolor={
                    activeShape === label ? COLORS.LIGHT_GRAY : COLORS.WHITE
                  }>
                  <IconButton
                    disableRipple
                    onClick={() => handleSetActiveShape(label)}>
                    <Icon sx={{ color: 'black' }} />
                    <Typography ml=".4375rem" fontSize=".75rem">
                      {label}
                    </Typography>
                  </IconButton>
                </Box>
              );
            })}
          </Box>
        )}

        {isPenVisible && (
          <Box
            ref={shapesRef}
            sx={styles.canvasPen}
            onClick={(event: any) => {
              event.stopPropagation();
            }}
            border={`1px solid ${theme.palette.divider}`}>
            <Slider
              aria-label="Volume"
              min={1}
              max={20}
              value={penSize}
              onChange={handleSlider}
            />
          </Box>
        )}

        {isColorPaletteOpen && (
          <Box
            ref={paletteRef}
            position="absolute"
            left="5rem"
            top="17.5rem"
            onClick={(event: any) => {
              event.stopPropagation();
            }}
            zIndex={10}>
            <SketchPicker color={color} onChangeComplete={handleSelectColor} />
          </Box>
        )}

        {isLayersVisible && (
          <Box
            ref={shapesRef}
            sx={styles.canvaslayers}
            onClick={(event: any) => {
              event.stopPropagation();
            }}
            border={`1px solid ${theme.palette.divider}`}>
            {selectedImage?.strokes.map((layerStrokes, index) => {
              //draw strokesLayer on a small canvas
              return (
                <Box
                  sx={styles.strokesLayer}
                  key={'label'}
                  onClick={() => setStrokesLayer(index)}
                  bgcolor={
                    index === strokesLayer ? COLORS.LIGHT_GRAY : COLORS.WHITE
                  }>
                  {!index ? 'Blank' : `Layer ${index}`}
                </Box>
              );
            })}
          </Box>
        )}

        <Box sx={styles.remotePanel}>
          <Box sx={styles.canvasArea}>
            <Box
              display={isImageLoading ? 'flex' : 'none'}
              sx={styles.canvasProgress}>
              <CircularProgress sx={{ color: COLORS.PRIMARY }} />
            </Box>

            <Box
              sx={{
                ...(styles.editorCanvasWrapper as React.CSSProperties),
                display: isImageLoading ? 'none' : 'flex',
                position: 'relative',
              }}>
              <CanvasImage />
              <CanvasSketch />
              <Box sx={styles.imageDimensions}>
                {Math.round(selectedImage?.width || 0)} ×{' '}
                {Math.round(selectedImage?.height || 0)}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default CanvasView;
